/**
 * Build the sidebar nav entries for a user's saved quick links. Returns an
 * empty section when nothing is configured so callers can spread the result
 * unconditionally.
 *
 * Quick Links *promote* a destination to the top of the nav rather than
 * adding a second copy of it. Pass ``navItems`` (everything the sidebar would
 * otherwise render) and the returned ``promotedHrefs`` tells the caller which
 * entries to drop from their usual position — so saving "Membership" as a
 * Quick Link visibly moves it up instead of doing nothing, which is what it
 * used to do when duplicates were simply filtered out.
 *
 * A promoted entry reuses the real nav item (icon, label, badge) so it looks
 * identical in its new position. Links that don't correspond to a nav item —
 * a sub-tab, or a page hidden from this sidebar — fall back to the catalog
 * entry's icon and label.
 *
 * A promoted entry also keeps the nav item's own `id` rather than minting a
 * fresh one. `useSidebarPins` persists a user's compact-sidebar pin order as
 * a list of item ids; giving the promoted item a new id would make an
 * existing pin unresolvable (it silently drops off the pinned bar) while the
 * promoted item itself lands in the "More" overflow — the opposite of what
 * promoting it is supposed to do.
 */
import { Star } from 'lucide-react';
import type { QuickLink } from '@/api/uiPreferences';
import { resolveQuickLinks } from '@/components/sidebar/resolveQuickLinks';
import type { SidebarNavItem } from '@/components/sidebar/types';
import type { FeatureFlags } from '@/config/feature-flags';
import type { PermissionAction, PermissionFeature } from '@/utils/permissions';

interface BuildArgs {
  links: QuickLink[];
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean;
  clientId: string;
  flags?: FeatureFlags | null;
  /** Every item the sidebar would render, so matching entries can be hoisted. */
  navItems?: ReadonlyArray<SidebarNavItem>;
}

export interface QuickLinksSection {
  /** Section header plus one row per saved link, ready to render at the top. */
  items: SidebarNavItem[];
  /** Hrefs now shown under Quick Links — callers drop these from the main nav. */
  promotedHrefs: ReadonlySet<string>;
}

const EMPTY_SECTION: QuickLinksSection = { items: [], promotedHrefs: new Set<string>() };

export function buildQuickLinksSection({
  links,
  hasPermission,
  clientId,
  flags,
  navItems,
}: BuildArgs): QuickLinksSection {
  const resolved = resolveQuickLinks(links, hasPermission, clientId, flags);
  if (resolved.length === 0) return EMPTY_SECTION;

  const navItemsByHref = new Map<string, SidebarNavItem>();
  for (const item of navItems ?? []) {
    if (item.href && item.href !== '#' && !item.disabled && !navItemsByHref.has(item.href)) {
      navItemsByHref.set(item.href, item);
    }
  }

  const items: SidebarNavItem[] = [
    {
      name: '--- QUICK LINKS HEADER ---',
      icon: Star,
      href: '#',
      isSectionHeader: true,
      sectionTitle: 'Quick Links',
    },
  ];
  const promotedHrefs = new Set<string>();

  for (const { link, catalog, href } of resolved) {
    const navItem = navItemsByHref.get(href);
    if (navItem) {
      promotedHrefs.add(href);
      items.push({
        ...navItem,
        id: navItem.id ?? `ql-${link.id}`,
        name: link.label || navItem.name,
        compactGroup: 'primary',
      });
      continue;
    }
    items.push({
      id: `ql-${link.id}`,
      name: link.label || catalog.label,
      icon: catalog.icon,
      href,
      compactGroup: 'primary',
      requiredPermission: catalog.requiredPermission,
    });
  }

  return { items, promotedHrefs };
}
